import axios from "axios";
import { emojiToUnicode } from "../constants/reaction-unicode";

/**
 * Add a reaction
 * @param {Object} params - { userId, reactableType, reactableId, reaction }
 * @returns {Promise<Object>} The created reaction
 */
export const addReaction = async ({ userId, reactableType, reactableId, reaction }) => {
    try {
        const response = await axios.post("/api/reactions", {
            userId,
            reactableType, 
            reactionType: emojiToUnicode(reaction),
        }, {
            params: {
                propertyId: reactableId,
            },
        });
        return response.data;
    } catch (error) {
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        throw new Error(error.response?.data?.message || "Failed to add reaction");
    }
};

/**
 * Remove a reaction
 * @param {Object} params - { userId, reactableType, reactableId, reaction }
 * @returns {Promise<Object>} The server response
 */
export const removeReaction = async ({ userId, reactableType, reactableId, reaction }) => {
    try {
        const response = await axios.delete("/api/reactions", {
            data: {
                userId,
                reactableType,
                reactionType: emojiToUnicode(reaction),
            },
            params: {
                propertyId: reactableId,
            },
        });
        return response.data;
    } catch (error) {
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        throw new Error(error.response?.data?.message || "Failed to remove reaction");
    }
};

/**
 * Get reaction counts for a reactable
 * @param {string} reactableType
 * @param {string} reactableId
 * @returns {Promise<Object>} Reaction counts
 */
export const getReactionCounts = async (reactableType, reactableId) => {
    try {
        const response = await axios.get(`/api/reactions/${reactableType}/${reactableId}/count`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to fetch reaction counts");
    }
};

/**
 * Fetch user reactions for a specific property (listing)
 *
 * @param {string} userId - The ID of the user reacting
 * @param {string} propertyId - The ID of the property (listing)
 * @param {string} [order="ASC"] - Optional order by created time
 * @returns {Promise<Array>} List of reactions
 */
export async function getUserReactionsForProperty(userId, propertyId, order = "ASC") {
    const response = await axios.get(`/api/reactions/${userId}/listing`, {
        params: {
            propertyId,
            order,
        },
    });
    return response.data;
}

/**
 * Fetch listing reactions with pagination support
 *
 * @param {string} reactableType - The type of reactable (e.g., "Property")
 * @param {string} reactableId - The ID of the reactable entity (e.g., propertyId)
 * @param {string|null} reactionType - Optional reaction type filter (Unicode format)
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Paginated reactions with user info
 */
export const getListingReactions = async (reactableType, reactableId, reactionType = null, page = 1, limit = 10) => {
    try {
        const params = {
            page,
            limit,
        };
        
        if (reactionType) {
            params.reactionType = reactionType;
        }
        
        const response = await axios.get(`/api/reactions/${reactableType}/${reactableId}/users`, {
            params,
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to fetch listing reactions");
    }
};
